import { useEffect, useState, useLayoutEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { CTA, trackAttrs } from '../utils/track'
import { BOOK_CALL_URL } from '../config/cta'
import { FaArrowRight, FaLightbulb, FaHandshake } from 'react-icons/fa'
import ImageLazy from '../components/ui/ImageLazy/ImageLazy'
import InlineHighlight from '../components/content/InlineHighlight/InlineHighlight'
import HeroArtGrid from '../components/sections/HeroArtGrid/HeroArtGrid'
import { coversFrom } from '../utils/coverUrl'
import { MENU } from '../config/MENU'
import type { IMenuItem } from '../config/MENU'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'
import InlineSubscribe from '../components/content/InlineSubscribe/InlineSubscribe'

export default function LearningsPage({ items = [] }: { items?: any[] }) {
  const allArticles = items
  const [selectedCategory, setSelectedCategory] = useState<string>('')

  useLayoutEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header') as HTMLElement | null
      const stickyNavEl = document.querySelector('.sticky.top-0') as HTMLElement | null
      const headerH = headerEl?.offsetHeight ?? 0
      const navH = stickyNavEl?.offsetHeight ?? 0
      const offset = headerH + navH
      document.documentElement.style.setProperty('--header-nav-height', `${offset}px`)
    }

    measure()
    requestAnimationFrame(() => measure())
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const filterOptions: { value: string; label: string }[] = [
    { value: '', label: 'All' },
    ...(MENU.LEARNING.items || []).map((item: IMenuItem) => ({
      value: (item.path || '').replace(/^\//, ''),
      label: item.title || '',
    })),
  ].filter((opt) => opt.label)

  const filteredArticles =
    selectedCategory === ''
      ? allArticles
      : allArticles.filter((a) => a.category === selectedCategory)

  const heroArt = coversFrom(allArticles)

  return (
    <div className="min-h-screen bg-gray-50 text-[#232323]">

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-brand-navy py-20 md:py-28">
        <ContentColumn anchor="learnings-hero" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="max-w-3xl">
            <span className="mb-8 inline-flex w-fit items-center gap-2 font-body text-[15px] text-gray-50/80 leading-[1.58] tracking-[-0.003em]">
              <FaLightbulb className="text-base" aria-hidden="true" />
              {allArticles.length}+ Learning Resources Available
            </span>

            <h1 className="font-sans text-[42px] sm:text-[48px] md:text-[56px] font-bold text-gray-50 mb-6 leading-[1.3] tracking-[-0.02em]">
              Things I've <InlineHighlight color="red">learned</InlineHighlight>
            </h1>

            <p className="text-[21px] leading-[1.58] text-gray-50/85 font-body mb-12 tracking-[-0.003em]">
              Everything I know about frontend, backend, system design, and engineering leadership -- organized so you can learn it faster than I did.
            </p>

            <a
              href="#articles"
              className="group inline-flex items-center justify-center gap-2 bg-gray-50 text-gray-900 font-heading font-bold px-8 py-4 rounded-smooth hover:bg-gray-50/95 transition-colors text-[17px] tracking-[-0.02em]"
            >
              Browse Articles
              <FaArrowRight
                className="text-sm transition-transform duration-150 group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </a>
          </div>
          <HeroArtGrid images={heroArt} />
          </div>
        </ContentColumn>
      </section>

      {/* Breadcrumb */}
      <section className="border-b border-border-subtle bg-gray-50">
        <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-4">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-label font-body text-gray-900/60">
              <li>
                <Link
                  to="/"
                  className="transition-colors hover:text-gray-900 focus:text-gray-900 focus:outline-none"
                >
                  Home
                </Link>
              </li>
              <li className="font-heading text-gray-900">/</li>
              <li className="font-heading text-gray-900" aria-current="page">
                Learnings
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="border-b border-gray-200 bg-white/95 backdrop-blur-sm py-3">
        <ContentColumn anchor="learnings-filter" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {filterOptions.map((opt) => (
              <button
                key={opt.value || 'all'}
                onClick={() => setSelectedCategory(opt.value)}
                className={`px-4 py-2 rounded-smooth text-sm font-heading font-semibold uppercase tracking-[0.08em] transition-colors duration-150 ${
                  selectedCategory === opt.value
                    ? 'bg-brand-navy text-white'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </ContentColumn>
      </section>

      {/* Article Grid */}
      <section id="articles" className="py-16 md:py-24">
        <ContentColumn anchor="learnings-list" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredArticles.map((article: any) => {
              const categoryPath = article.category || ''
              const articleUrl = categoryPath && article.slug ? `/${categoryPath}/${article.slug}` : '/learnings'

              return (
                <Link
                  key={article.id || article.slug}
                  to={articleUrl}
                  className="group flex h-full flex-col overflow-hidden rounded-smooth-lg border-2 border-gray-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_rgba(0,0,0,0.12)]"
                >
                  {(article.image?.url || article.image?.formats?.small?.url) && (
                    <div className="relative aspect-video flex-shrink-0 overflow-hidden">
                      <ImageLazy
                        src={article.image?.formats?.small?.url || article.image?.url}
                        alt={article.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      {new Date().getTime() - new Date(article.publishedAt || article.createdAt).getTime() <
                        1000 * 60 * 60 * 24 * 7 && (
                        <span className="absolute left-2 top-2 rounded-smooth bg-red-600 px-2 py-1 font-heading text-[0.65rem] font-bold uppercase text-white">
                          NEW
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <span className="mb-2 font-body text-xs uppercase tracking-[0.18em] text-gray-900/50">
                      {filterOptions.find((o) => o.value === article.category)?.label || article.category}
                    </span>
                    <h3 className="mb-3 line-clamp-2 font-heading text-lg font-bold uppercase leading-tight text-gray-900">
                      {article.title}
                    </h3>
                    <div className="mt-auto flex items-center justify-between border-t border-gray-900/10 pt-4">
                      <span className="font-body text-xs uppercase tracking-[0.18em] text-gray-900/50">
                        {new Date(article.publishedAt || article.createdAt).toLocaleDateString('en-US', {
                          timeZone: 'UTC',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      <FaArrowRight
                        className="text-sm text-gray-900 transition-transform duration-200 group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>

          {filteredArticles.length === 0 && (
            <p className="py-16 text-center font-body text-gray-900/70">
              No articles in this category yet.
            </p>
          )}
        </ContentColumn>
      </section>

      {/* Closing block: discovery call, mentorship, and the email capture in one
          section. These were two sections, which left a dead gap between a white
          panel and this navy one and read as two separate endings to the page.
          The subscribe form is the lower-commitment fallback for anyone not ready
          to book, so it sits under the buttons behind a divider. */}
      <section className="relative overflow-hidden bg-brand-navy py-20 text-white md:py-28">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-cyan-500/20 to-teal-500/20" />
          </div>
          <div className="absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-blue-400/20 to-cyan-500/20 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 h-[600px] w-[600px] rounded-full bg-gradient-to-tr from-teal-500/15 to-blue-500/15 blur-3xl" />
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-gray-900/30 to-gray-900/80" />
        </div>

        <ContentColumn anchor="learnings-cta" className="relative z-10 mx-auto max-w-5xl px-4 text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-smooth border border-gray-50/20 bg-brand-cream/10 px-4 py-1 font-heading text-[0.65rem] uppercase tracking-[0.24em] text-white">
            <FaHandshake className="text-sm" aria-hidden="true" />
            Keep Advancing
          </div>
          <h2 className="mb-6 font-sans text-[42px] font-bold leading-[1.3] tracking-[-0.02em] text-white sm:text-[48px] md:text-[56px]">
            Want to go <InlineHighlight color="red">deeper</InlineHighlight>?
          </h2>
          <p className="mx-auto mb-12 max-w-2xl font-body text-[21px] leading-[1.58] tracking-[-0.003em] text-white/85">
            Reading is a start. If you want focused, one-on-one guidance on your specific challenges, let's talk.
          </p>

          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              to={BOOK_CALL_URL}
              {...trackAttrs(CTA.bookCall, 'learnings_page')}
              className="group inline-flex items-center justify-center gap-2 rounded-lg bg-brand-cream px-8 py-4 font-heading text-[17px] font-bold tracking-[-0.02em] text-gray-900 transition-colors hover:bg-brand-cream/95"
            >
              Schedule Discovery Call
              <FaArrowRight
                className="text-base transition-transform duration-200 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Link>
            <Link
              to="/mentorship"
              {...trackAttrs(CTA.mentorship, 'learnings_page')}
              className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-gray-50/40 bg-transparent px-8 py-4 font-heading text-[17px] font-bold tracking-[-0.02em] text-white transition-colors hover:border-gray-50/60 hover:bg-gray-50/10"
            >
              Explore Mentorship
            </Link>
          </div>

          <div className="mx-auto mt-14 max-w-xl border-t border-gray-50/20 pt-10">
            <InlineSubscribe source="learnings" variant="dark" />
          </div>
        </ContentColumn>
      </section>
    </div>
  )
}
